/**
 * Browser-tab identity (SPEC-009): title grammar, environment prefix and product favicon.
 *
 * Pure — no React, no DOM — so every rule here is unit-tested directly
 * (utilities/__tests__/tabIdentity.test.js) and shared by every page through
 * components/Navigation/TabIdentity.jsx.
 *
 * Title grammar (§3.1):   [ENV] <content> · <port>
 *   - content first; no "OMC" / "OMCi" / product-name prefixes — the favicon carries the product
 *   - the port is appended on port-scoped pages only, and never when the content already names it
 *   - identifiers that are user data (vessel, port) are wrapped in bidi isolates (U+2068 … U+2069)
 *   - the environment prefix goes first so it survives truncation
 *
 * Favicon (§3.3): resolved from the route, the dashboard page and the #hash ONLY — never from
 * entitlements, cookies or navigation history, so every open tab resolves independently:
 *   D6         — pages that show a passage plan: plan results (#show_<id>, any tab), plan history and
 *                revisions (#show_plan_history_<id>, #historical_…) and the new-plan form (#new_plan);
 *                voyage plan pages join this list when SPEC-008 lands (PO 2026-09-24)
 *   SO         — Schedule (planned view, Forms tab, Berth Draft)
 *   OMCi ship  — everything else: Home, Weather, Analyse and any other layout page, the Plan list and
 *                drafts, Monitor (overview and vessel detail), Vessels, Account, Help, Region settings,
 *                Administration, and every page outside the dashboard (Launch Pad, Terms, sign-in, admin)
 * The Hub widget's own currentApp() (menu-row highlight) is coarser on purpose and stays separate.
 */
import { normaliseEnvTag } from "utilities/envTag";

export { normaliseEnvTag };

/** Bump when the artwork in public/favicons changes so browsers refetch (§3.5). */
export const ICON_VERSION = "3";

export const PRODUCT_KEYS = ["omci", "d6", "so", "d5", "pw", "ta", "ba", "kc"];

export function iconLinks(product) {
    const key = PRODUCT_KEYS.includes(product) ? product : "omci";
    const version = `?v=${ICON_VERSION}`;
    return {
        png16: `/favicons/${key}-16.png${version}`,
        png32: `/favicons/${key}-32.png${version}`,
        svg: `/favicons/${key}.svg${version}`,
    };
}

// Bidirectional isolation (Figma 04): an RTL vessel or port name cannot reorder the template.
const FSI = "⁨";
const PDI = "⁩";
export const isolate = (text) => (text == null || text === "" ? "" : `${FSI}${text}${PDI}`);
const plain = (text) => String(text == null ? "" : text).replace(/[⁨⁩]/g, "");

/** English source templates; the translated ones live in common.json → tab_title.* */
export const DEFAULT_TEMPLATES = {
    "common:tab_title.format": "{{content}} · {{port}}",
    "common:tab_title.env_format": "[{{tag}}] {{title}}",
};

function fill(template, vars) {
    return String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => (vars && vars[name] != null ? String(vars[name]) : ""));
}

/**
 * Build the final document title.
 * @param {object} args
 * @param {string} args.content   translated page content (may already contain isolated identifiers)
 * @param {string|null} [args.port]  configured port display name; omitted when null/empty or already in content
 * @param {string|null} [args.envTag] "DEMO" | "STG" | "DR" | null
 * @param {(key: string, vars: object) => string|null} [args.translate]
 *        returns the translated template or null when the key is not loaded → English default
 */
export function composeTitle({ content, port = null, envTag = null, translate = null }) {
    const tr = (key, vars) => {
        const translated = typeof translate === "function" ? translate(key, vars) : null;
        return typeof translated === "string" && translated.trim() !== "" ? translated : fill(DEFAULT_TEMPLATES[key], vars);
    };
    let title = String(content == null ? "" : content).trim();
    const portText = String(port == null ? "" : port).trim();
    if (portText !== "" && !plain(title).toLowerCase().includes(portText.toLowerCase())) {
        title = tr("common:tab_title.format", { content: title, port: isolate(portText) });
    }
    if (envTag) title = tr("common:tab_title.env_format", { tag: envTag, title });
    return title;
}

// ---- product resolution ------------------------------------------------------------------

const SO_PAGE_IDS = ["schedule_optimiser", "schedule"];
const PLAN_HASHES = ["new_plan", "plan_drafts", "page_1", "page_2", "page_3", "page_4"];

const lower = (value) => String(value == null ? "" : value).trim().toLowerCase();
const stripHash = (hash) => lower(hash).replace(/^#/, "");

/**
 * Admin-defined layout pages (Home, Weather, Analyse, …) are Hub workspaces → OMCi ship (§1.3 O2,
 * settled 2026-09-24). Kept as a function so the exception list has one home if a layout page
 * ever belongs to a product.
 */
export function productForLayoutPage(name) { // eslint-disable-line no-unused-vars
    return "omci";
}

/** A #hash that shows a passage plan: results, history / revisions, or the new-plan form. */
export function isPassagePlanHash(hash) {
    const h = stripHash(hash);
    return h.startsWith("show") || h.startsWith("historical_") || h === "new_plan";
}

/**
 * The dashboard's string page ids derived from a #hash, mirroring changeRoute() in
 * pages/users/dashboard.jsx; null for hashes that name an admin-defined layout page (or none).
 */
export function pageIdFromHash(hash) {
    const h = stripHash(hash);
    if (h === "") return null;
    if (SO_PAGE_IDS.includes(h)) return "schedule_optimiser";
    if (["plan", "monitor", "account", "help", "administration", "cards", "search_vessels", "new_vessels", "edit_vessels", "view_vessels"].includes(h)) return h;
    if (PLAN_HASHES.includes(h) || h.startsWith("show") || h.startsWith("historical_")) return "plan";
    return null;
}

/**
 * Favicon for a portal location. `pageId` is the dashboard's page id (string built-in page or
 * the numeric id of a layout page, whose `pageName` is then needed); `hash` covers the moment
 * before the layout has resolved.
 */
export function productForRoute({ pathname = "", pageId = null, pageName = null, hash = "" } = {}) {
    const path = String(pathname || "");
    if (!path.startsWith("/users/dashboard")) return "omci";

    const id = pageId == null || pageId === "" ? pageIdFromHash(hash) : lower(pageId);
    if (id == null || id === "") return productForLayoutPage(pageName);
    if (SO_PAGE_IDS.includes(id)) return "so";
    if (id === "plan" && isPassagePlanHash(hash)) return "d6";
    return "omci";
}

// ---- dashboard title -----------------------------------------------------------------------

const capitalise = (value) => (value ? value[0].toUpperCase() + value.slice(1) : value);

/**
 * Title and favicon for the user dashboard (§4 rows). Returns { product, content, port }.
 * `t` is the page's translator; the templates come from common.json → tab_title.*
 */
export function dashboardTitle(
    {
        pageId = null,
        hash = "",
        pages = [],
        plan = null,
        showIndividualPlan = false,
        showHistoricalPlan = false,
        showPlanHistory = false,
        selectedVessel = null,
        portName = null,
    } = {},
    t,
) {
    const h = stripHash(hash);
    const port = portName == null || String(portName).trim() === "" ? null : String(portName);
    const layoutPage = pageId != null && pageId !== "" ? (pages || []).find((p) => String(p.id) === String(pageId)) : null;
    const pageName = layoutPage ? layoutPage.name : null;
    const id = layoutPage ? null : pageId == null || pageId === "" ? pageIdFromHash(h) : lower(pageId);
    const product = productForRoute({ pathname: "/users/dashboard", pageId: layoutPage ? layoutPage.id : id, pageName, hash: h });
    const planVessel = plan && plan.name ? isolate(String(plan.name)) : "";
    const vessel = selectedVessel && selectedVessel.name ? isolate(String(selectedVessel.name)) : "";
    const result = (content, withPort = true) => ({ product, content, port: withPort ? port : null });

    if (layoutPage) return result(t(`sidebar:${pageName}`));
    if (id == null) return result(t("sidebar:Home"));

    switch (id) {
        case "schedule_optimiser":
            return result(t("sidebar:Schedule Optimiser"));
        case "plan": {
            const history = showPlanHistory || h.startsWith("show_plan_history") || h.startsWith("historical_");
            if (history) return result(planVessel ? t("common:tab_title.plan_history", { vessel: planVessel }) : t("sidebar:Plan"));
            if (planVessel && (showIndividualPlan || showHistoricalPlan || h.startsWith("show"))) {
                return result(t("common:tab_title.passage_plan", { vessel: planVessel }));
            }
            if (h === "new_plan") return result(t("common:tab_title.new_plan"));
            if (h === "plan_drafts") return result(t("common:tab_title.plan_drafts"));
            return result(t("sidebar:Plan"));
        }
        case "monitor":
            return result(t("sidebar:Monitor"));
        case "search_vessels":
            return result(t("sidebar:Search Vessels"));
        case "new_vessels":
            return result(t("sidebar:New Vessel"));
        case "view_vessels":
            return result(vessel ? t("common:tab_title.vessel_details", { vessel }) : t("sidebar:View_vessels"));
        case "edit_vessels":
            return result(vessel ? t("common:tab_title.edit_vessel", { vessel }) : t("sidebar:Edit_vessels"));
        case "account":
            return result(t("sidebar:account_settings"), false);
        case "help":
            return result(t("sidebar:help"), false);
        case "administration":
            return result(t("sidebar:administration"), false);
        case "region":
            return result(t("sidebar:region_settings"), false);
        default:
            return result(t(`sidebar:${capitalise(id)}`));
    }
}
